import { MENTION_KINDS, MEMORY_MODIFIERS, MEMORY_SCOPE_MODIFIERS, MEMORY_TEMP_MODIFIERS, TABLE_MODIFIERS, } from "./types.js";
/**
 * Single-pass scanner for `#kind/slug[:modifier]` tokens.
 *
 * Grammar + boundary rules: technical-design.md §1. Highlights:
 *   - token must start at start-of-text or after a non-word char
 *   - base slug alphabet `[a-z0-9-]`; `memory`/`context` additionally
 *     allow `_` (their slugs are existing immutable snake_case keys)
 *   - no leading/trailing `-`/`_`, 1–64 chars
 *   - `:modifier` valid on `table` only
 *   - known-kind near-misses are reported as `malformed`, never thrown
 */
export const MENTION_SLUG_MAX = 64;
const KIND_SET = new Set(MENTION_KINDS);
const MODIFIER_SET = new Set(TABLE_MODIFIERS);
const MEMORY_MODIFIER_SET = new Set(MEMORY_MODIFIERS);
const MEMORY_TEMP_SET = new Set(MEMORY_TEMP_MODIFIERS);
const MEMORY_SCOPE_SET = new Set(MEMORY_SCOPE_MODIFIERS);
const WORD_CHAR = /[A-Za-z0-9_]/;
const LOWER_WORD = /[a-z]/;
function slugAlphabet(kind) {
    // memory/context slugs are variable names; action slugs are host-chosen
    // frontend-action names (typically snake_case, e.g. add_to_cart).
    return kind === "memory" || kind === "context" || kind === "action"
        ? /[a-z0-9_-]/
        : /[a-z0-9-]/;
}
export function parseMentions(text) {
    const mentions = [];
    const malformed = [];
    let i = 0;
    while (i < text.length) {
        const hash = text.indexOf("#", i);
        if (hash === -1)
            break;
        i = hash + 1;
        if (hash > 0 && WORD_CHAR.test(text[hash - 1]))
            continue;
        const kindMatch = matchKind(text, hash + 1);
        if (!kindMatch)
            continue;
        const { kind, slugStart } = kindMatch;
        const alphabet = slugAlphabet(kind);
        let j = slugStart;
        while (j < text.length && alphabet.test(text[j]))
            j++;
        // Trailing separators belong to prose ("…see #skill/foo-" reads as
        // punctuation), and slugs can never end in `-`/`_` anyway.
        let slugEnd = j;
        while (slugEnd > slugStart && /[-_]/.test(text[slugEnd - 1]))
            slugEnd--;
        const slug = text.slice(slugStart, slugEnd);
        const report = (end, reason) => {
            malformed.push({ raw: text.slice(hash, end), start: hash, end, reason });
            i = end;
        };
        if (slug.length === 0) {
            report(slugStart, "empty_slug");
            continue;
        }
        if (slug.length > MENTION_SLUG_MAX) {
            report(slugEnd, "slug_too_long");
            continue;
        }
        // Optional `:modifier` — only meaningful when the colon is glued to the
        // slug/previous segment and starts a lowercase word; otherwise prose.
        // Tables take one segment (access level). Memory takes up to two,
        // order-independent: an access mode (read/write/both) and a temp mode
        // (permanent/use/ask), classified by which set each word is in.
        let end = slugEnd;
        let modifier;
        let tempModifier;
        let scopeModifier;
        // Reads the next `:word` if one is glued on; returns null if there's none.
        const nextSegment = (from) => {
            if (text[from] !== ":" || from + 1 >= text.length || !LOWER_WORD.test(text[from + 1])) {
                return null;
            }
            let w = from + 1;
            while (w < text.length && LOWER_WORD.test(text[w]))
                w++;
            return { word: text.slice(from + 1, w), wordEnd: w };
        };
        // a trimmed separator before `:` means prose, e.g. "#table/x-:write"
        if (slugEnd === j) {
            let bad = false;
            let cursor = slugEnd;
            const maxSegments = kind === "memory" ? 3 : 1;
            for (let n = 0; n < maxSegments; n++) {
                const seg = nextSegment(cursor);
                if (!seg)
                    break;
                const { word, wordEnd } = seg;
                if (kind === "table") {
                    if (MODIFIER_SET.has(word)) {
                        modifier = word;
                        end = wordEnd;
                    }
                    else {
                        report(wordEnd, "invalid_modifier");
                        bad = true;
                    }
                    break;
                }
                else if (kind === "memory") {
                    if (MEMORY_MODIFIER_SET.has(word) && modifier === undefined) {
                        modifier = word;
                        end = wordEnd;
                        cursor = wordEnd;
                    }
                    else if (MEMORY_TEMP_SET.has(word) && tempModifier === undefined) {
                        tempModifier = word;
                        end = wordEnd;
                        cursor = wordEnd;
                    }
                    else if (MEMORY_SCOPE_SET.has(word) && scopeModifier === undefined) {
                        scopeModifier = word;
                        end = wordEnd;
                        cursor = wordEnd;
                    }
                    else if (MODIFIER_SET.has(word) && !MEMORY_MODIFIER_SET.has(word)) {
                        report(wordEnd, "invalid_modifier");
                        bad = true;
                        break;
                    }
                    else {
                        // unknown / duplicate-dimension word ends the token as prose
                        break;
                    }
                }
                else {
                    if (MODIFIER_SET.has(word) || MEMORY_MODIFIER_SET.has(word)) {
                        report(wordEnd, "modifier_not_allowed");
                        bad = true;
                    }
                    // else ordinary prose colon, e.g. "#skill/x: it does y"
                    break;
                }
            }
            if (bad)
                continue;
        }
        mentions.push({
            raw: text.slice(hash, end),
            kind,
            slug,
            ...(modifier ? { modifier } : {}),
            ...(tempModifier ? { tempModifier } : {}),
            ...(scopeModifier ? { scopeModifier } : {}),
            start: hash,
            end,
        });
        i = end;
    }
    return { mentions, malformed };
}
function matchKind(text, pos) {
    const slash = text.indexOf("/", pos);
    if (slash === -1 || slash - pos > 7)
        return null;
    const word = text.slice(pos, slash);
    if (!KIND_SET.has(word))
        return null;
    return { kind: word, slugStart: slash + 1 };
}
export function serializeMention(m) {
    return `#${m.kind}/${m.slug}${m.modifier ? `:${m.modifier}` : ""}${m.tempModifier ? `:${m.tempModifier}` : ""}${m.scopeModifier ? `:${m.scopeModifier}` : ""}`;
}
/**
 * Collapses repeated `(kind, slug)` mentions into one entry per attachment.
 * Table grants union upward (`write` ⊃ `update`/`insert` ⊃ select): the
 * surviving entry carries the widest modifier seen. `skill` and `tool` are
 * one namespace (aliases over the same slug space) and dedupe together,
 * keeping the first-seen kind spelling.
 */
export function dedupeMentions(ms) {
    const order = [];
    const byKey = new Map();
    const rank = (m) => (m.modifier === "write" ? 3 : m.modifier ? 1 : 0);
    for (const m of ms) {
        const ns = m.kind === "tool" ? "skill" : m.kind;
        const key = `${ns}/${m.slug}`;
        const prev = byKey.get(key);
        if (!prev) {
            order.push(key);
            byKey.set(key, m);
            continue;
        }
        if (m.kind === "table" && m.modifier && m.modifier !== prev.modifier) {
            // insert+update (distinct grains) escalate to write; otherwise widest wins
            const widest = prev.modifier && rank(m) === rank(prev) && m.modifier !== prev.modifier
                ? "write"
                : rank(m) > rank(prev)
                    ? m.modifier
                    : prev.modifier;
            byKey.set(key, { ...prev, modifier: widest });
        }
    }
    return order.map((k) => byKey.get(k));
}
//# sourceMappingURL=parser.js.map